import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useLogin } from "@/hooks/api/auth";
import { isAxiosError } from "@/lib/api";
import { getTenantSlug, urlForTenant } from "@/lib/tenant";
import { useAuthStore } from "@/stores/auth";
import type { Role } from "@/types/api";

const schema = z.object({
  email: z.string().email("Invalid email"),
  password: z.string().min(1, "Required"),
});

type FormValues = z.infer<typeof schema>;

// EMPLOYEE-role users land on the ESS portal; HR-side users (incl. AUDITOR
// for read-only review) land on /dashboard.
function landingPathForRole(role: Role | null | undefined): string {
  if (role === "EMPLOYEE") return "/ess";
  return "/dashboard";
}

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const tenantSlug = getTenantSlug();
  const user = useAuthStore((s) => s.user);
  const role = useAuthStore((s) => s.role);
  const login = useLogin();

  const [serverError, setServerError] = useState<string | null>(null);
  const [memberships, setMemberships] = useState<
    { tenant_slug: string; tenant_name: string }[] | null
  >(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  useEffect(() => {
    if (user) navigate(landingPathForRole(role), { replace: true });
  }, [user, role, navigate]);

  if (user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? landingPathForRole(role)} replace />;
  }

  const onSubmit = async (data: FormValues) => {
    setServerError(null);
    setMemberships(null);
    try {
      const result = await login.mutateAsync(data);
      if (result.kind === "session") {
        navigate(landingPathForRole(result.session.role), { replace: true });
      } else {
        setMemberships(result.memberships);
      }
    } catch (err) {
      if (isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 401) setServerError(t("auth.invalid_credentials"));
        else if (status === 403)
          setServerError(
            (err.response?.data as { detail?: string })?.detail ??
              t("auth.no_membership")
          );
        else setServerError(t("common.error_generic"));
      } else {
        setServerError(t("common.error_generic"));
      }
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-page px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">{t("auth.login_title")}</CardTitle>
          {tenantSlug && (
            <CardDescription>{t("app.tenant", { slug: tenantSlug })}</CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {memberships && (
            <div className="mb-4 rounded-md border border-info/30 bg-info-subtle p-4 text-sm">
              <p className="m-0 font-medium text-slate-900">
                {t("auth.multi_membership")}
              </p>
              <ul className="mt-2 space-y-1">
                {memberships.map((m) => (
                  <li key={m.tenant_slug}>
                    <a
                      href={urlForTenant(m.tenant_slug)}
                      className="text-primary hover:underline"
                    >
                      {m.tenant_name} ({m.tenant_slug})
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
              noValidate
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("auth.email")}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        autoComplete="email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("auth.password")}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="current-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {serverError && (
                <div
                  role="alert"
                  className="rounded-md border border-danger/30 bg-danger-subtle p-3 text-sm text-danger"
                >
                  {serverError}
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                loading={login.isPending}
              >
                {t("auth.login")}
              </Button>
            </form>
          </Form>

          <div className="mt-4 text-center text-sm">
            <Link to="/forgot-password" className="text-primary hover:underline">
              {t("auth.forgot")}
            </Link>
          </div>

          <div className="mt-6 text-center text-sm text-slate-600">
            {t("auth.no_account")}{" "}
            <Link to="/signup" className="text-primary hover:underline">
              {t("auth.start_trial")}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
